import {
  IntegrationServices,
  useQuery,
  useRunInvalidationForMutation
} from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  BulkActionsAction,
  useNotifications
} from '@sequencehq/core-components'
import { usePrevious } from '@sequencehq/utils'
import { useEffect, useMemo, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import {
  IMPORT_STALL_TIMEOUT_MS,
  PROGRESS_POLL_INTERVAL_MS,
  TIMED_OUT_MESSAGE,
  TIMED_OUT_TITLE
} from './customerImportsInFlight.constants'
import { CustomerImportFailure } from './customerImportsInFlight.types'
import { ImportRow } from './ImportRow'

export const useImportProgress = (props: {
  service: IntegrationServices
  customerIds: string[]
  customerNames: Record<string, string>
  startedAt?: string
}) => {
  const [hasStalled, setHasStalled] = useState(false)
  const hasStalledRef = useRef(false)

  const progressQuery = useQuery(
    dashboard20240730Client.postCreateCustomerFromExternalServiceProgress,
    {
      service: props.service,
      customerIds: props.customerIds,
      startedAt: props.startedAt
    },
    {
      refetchInterval: query => {
        const isTerminal =
          isImportComplete(query.state.data) || hasStalledRef.current
        return isTerminal ? false : PROGRESS_POLL_INTERVAL_MS
      }
    }
  )

  const totalCount = progressQuery.data?.totalCount ?? props.customerIds.length
  const currentCount = progressQuery.data?.currentCount ?? 0
  const failures = useMemo(
    () => progressQuery.data?.failures ?? [],
    [progressQuery.data?.failures]
  )

  const isComplete = isImportComplete(progressQuery.data)
  const isTerminal = isComplete || hasStalled

  useStallTimeout({
    isTerminal,
    currentCount,
    failedCount: failures.length,
    onStall: () => {
      hasStalledRef.current = true
      setHasStalled(true)
    }
  })

  const status = match({
    isTerminal,
    hasFailures: failures.length > 0,
    hasStalled
  })
    .with({ hasStalled: true }, () => 'COMPLETED_WITH_FAILURES' as const)
    .with({ isTerminal: false }, () => 'PROCESSING' as const)
    .with(
      { isTerminal: true, hasFailures: true },
      () => 'COMPLETED_WITH_FAILURES' as const
    )
    .with({ isTerminal: true, hasFailures: false }, () => 'COMPLETED' as const)
    .exhaustive()

  useInvalidateImportedCustomersOnTerminal(isTerminal)
  useNotifyOnStall(hasStalled)

  return {
    status,
    hasStalled,
    currentCount,
    totalCount,
    failedCount: failures.length,
    actions: buildActions({
      customerIds: props.customerIds,
      customerNames: props.customerNames,
      failures,
      currentCount
    })
  }
}

const buildActions = (args: {
  customerIds: string[]
  customerNames: Record<string, string>
  failures: CustomerImportFailure[]
  currentCount: number
}): BulkActionsAction[] => {
  const failuresById = new Map(
    args.failures.map(failure => [failure.externalId, failure])
  )
  const unsubmittedFailureIds = args.failures
    .map(failure => failure.externalId)
    .filter(id => !args.customerIds.includes(id))

  return [...args.customerIds, ...unsubmittedFailureIds].reduce<{
    actions: BulkActionsAction[]
    successesLeft: number
  }>(
    (acc, id) => {
      const title = (
        <ImportRow
          key={id}
          name={args.customerNames[id] ?? id}
          failure={failuresById.get(id)}
        />
      )

      if (failuresById.has(id)) {
        return {
          ...acc,
          actions: [...acc.actions, { id, title, status: 'failure' }]
        }
      }
      if (acc.successesLeft > 0) {
        return {
          actions: [...acc.actions, { id, title, status: 'success' }],
          successesLeft: acc.successesLeft - 1
        }
      }
      return {
        ...acc,
        actions: [...acc.actions, { id, title, status: 'pending' }]
      }
    },
    { actions: [], successesLeft: args.currentCount }
  ).actions
}

const isImportComplete = (
  progress:
    | {
        totalCount: number
        currentCount: number
        failures?: CustomerImportFailure[]
      }
    | null
    | undefined
) =>
  Boolean(progress) &&
  (progress?.currentCount ?? 0) + (progress?.failures?.length ?? 0) >=
    (progress?.totalCount ?? 0)

const useStallTimeout = (args: {
  isTerminal: boolean
  currentCount: number
  failedCount: number
  onStall: () => void
}) => {
  const onStallRef = useRef(args.onStall)
  onStallRef.current = args.onStall

  const { isTerminal, currentCount, failedCount } = args
  // biome-ignore lint/correctness/useExhaustiveDependencies: listed to restart the timer
  useEffect(() => {
    if (isTerminal) {
      return
    }
    const timer = setTimeout(
      () => onStallRef.current(),
      IMPORT_STALL_TIMEOUT_MS
    )
    return () => clearTimeout(timer)
  }, [isTerminal, currentCount, failedCount])
}

const useInvalidateImportedCustomersOnTerminal = (isTerminal: boolean) => {
  const invalidate = useRunInvalidationForMutation()
  const wasTerminal = usePrevious(isTerminal)

  useEffect(() => {
    if (wasTerminal || !isTerminal) {
      return
    }
    invalidate(dashboard20240730Client.postCreateCustomerFromExternalService, {
      refetchType: 'all'
    })
  }, [isTerminal, wasTerminal, invalidate])
}

const useNotifyOnStall = (hasStalled: boolean) => {
  const { displayNotification } = useNotifications()
  const hadStalled = usePrevious(hasStalled)

  useEffect(() => {
    if (hadStalled || !hasStalled) {
      return
    }
    displayNotification(TIMED_OUT_TITLE, {
      type: 'error',
      description: TIMED_OUT_MESSAGE
    })
  }, [hasStalled, hadStalled, displayNotification])
}
